/**
 * Google Places Address Input (web)
 *
 * Address autocomplete using the Maps JavaScript API (Places library) so it works in the
 * browser without CORS. Behavior and place shape match mobile GooglePlacesAutocomplete (address mode):
 * - Autocomplete (3+ chars), then Place Details for formatted_address and address_components.
 * - onPlaceSelected(place) receives { name, formattedAddress, address, city, state, country, postalCode, latitude, longitude }.
 *
 * GooglePlacesCityInput reuses the same autocomplete UI with (cities) predictions and buildCityPlaceData.
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { Input } from "./ui/input";
import { ErrorHandler } from "../utils/errorHandler";

const getApiKey = () => (process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "").trim();

/** True when the Places script can load (env set). */
export function isGooglePlacesConfigured() {
  return Boolean(getApiKey());
}

const SCRIPT_URL = "https://maps.googleapis.com/maps/api/js";

/** Cached promise so we only load the script once per key. */
let scriptLoadPromise = null;

function loadGoogleMapsScript(key) {
  if (typeof window === "undefined" || !key) return Promise.resolve(null);
  if (window.google?.maps?.places?.AutocompleteService) return Promise.resolve(window.google.maps.places);
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_URL}"]`);
    if (existing) {
      if (window.google?.maps?.places) return resolve(window.google.maps.places);
      existing.addEventListener("load", () => resolve(window.google?.maps?.places));
      return;
    }
    const script = document.createElement("script");
    script.src = `${SCRIPT_URL}?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google?.maps?.places ?? null);
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Google Maps script failed to load"));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

const REQUEST_DENIED_HINT =
  "Enable Places API in Google Cloud and add your site (e.g. http://localhost:3000/*) to API key HTTP referrer restrictions.";

/**
 * Find address component by type(s). Same logic as mobile GooglePlacesAutocomplete.
 * @param {Array} addressComponents - From Google Place Details address_components
 * @param {string|Array} types - e.g. 'street_number' or ['locality', 'sublocality_level_1']
 * @param {string} nameType - 'long_name' or 'short_name'
 * @returns {string}
 */
function findAddressComponent(addressComponents, types, nameType = "long_name") {
  if (!addressComponents || addressComponents.length === 0) return "";
  const typeArray = Array.isArray(types) ? types : [types];
  for (const type of typeArray) {
    for (const component of addressComponents) {
      if (component?.types && Array.isArray(component.types) && component.types.includes(type)) {
        const value = component[nameType];
        return value != null ? String(value) : "";
      }
    }
  }
  return "";
}

/**
 * Normalize PlaceResult from Maps JS API (location may be .lat()/.lng()) for buildPlaceData.
 */
function normalizePlaceForBuild(place) {
  if (!place) return place;
  const loc = place.geometry?.location;
  const lat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
  const lng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;
  return {
    ...place,
    geometry: loc ? { location: { lat, lng } } : undefined,
  };
}

/**
 * Build place payload from Place Details result. Shape matches mobile GooglePlacesAutocomplete (address mode).
 */
function buildPlaceData(place) {
  const normalized = normalizePlaceForBuild(place);
  const location = normalized.geometry?.location;
  const addressComponents = normalized.address_components || [];
  const streetNumber = findAddressComponent(addressComponents, "street_number");
  const route = findAddressComponent(addressComponents, "route");
  const state = findAddressComponent(addressComponents, "administrative_area_level_1", "short_name");
  const city = findAddressComponent(addressComponents, [
    "locality",
    "sublocality",
    "sublocality_level_1",
    "postal_town",
    "administrative_area_level_2",
  ]);
  const postalCode = findAddressComponent(addressComponents, "postal_code");
  const country = findAddressComponent(addressComponents, "country", "long_name");
  const address = streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber;
  return {
    name: normalized.name || null,
    formattedAddress: normalized.formatted_address || "",
    address: address || "",
    city,
    state,
    country,
    postalCode,
    latitude: location?.lat ?? null,
    longitude: location?.lng ?? null,
  };
}

/**
 * Locality-focused payload from Place Details (for city-only autocomplete, types (cities)).
 * cityName prefers Google's locality / postal_town so console slugs stay canonical (e.g. Paris).
 */
function buildCityPlaceData(place) {
  const normalized = normalizePlaceForBuild(place);
  const addressComponents = normalized.address_components || [];
  const locality = findAddressComponent(addressComponents, [
    "locality",
    "postal_town",
    "sublocality",
    "sublocality_level_1",
  ]);
  const cityName = (locality || normalized.name || "").trim();
  const regionLabel = findAddressComponent(addressComponents, "administrative_area_level_1", "long_name").trim();
  const country = findAddressComponent(addressComponents, "country", "long_name").trim();
  const displayName =
    cityName && country
      ? `${cityName}, ${country}`
      : cityName || (normalized.formatted_address || "").trim() || (normalized.name || "").trim();
  const location = normalized.geometry?.location;
  return {
    cityName,
    regionLabel,
    country,
    displayName,
    formattedAddress: normalized.formatted_address || "",
    placeId: normalized.place_id || null,
    latitude: location?.lat ?? null,
    longitude: location?.lng ?? null,
  };
}

const DETAIL_FIELDS = ["place_id", "name", "formatted_address", "geometry", "address_components"];

/**
 * Shared autocomplete + Place Details flow for address and city variants.
 */
function GooglePlacesAutocompleteCore({
  variant,
  value = "",
  onChange,
  onPlacePayload,
  placeholder,
  id,
  className,
  maxLength,
  "aria-label": ariaLabel,
  autoComplete = "off",
}) {
  const isCity = variant === "city";
  const minQueryLength = isCity ? 2 : 3;
  const logContext = isCity ? "GooglePlacesCityInput" : "GooglePlacesAddressInput";
  const noApiKeyMessage = isCity
    ? "Set REACT_APP_GOOGLE_PLACES_API_KEY to search and confirm cities with Google."
    : "Set REACT_APP_GOOGLE_PLACES_API_KEY for address autocomplete.";

  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placesReady, setPlacesReady] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [hasFocused, setHasFocused] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const placesServiceRef = useRef(null);
  const isSelectingRef = useRef(false);
  const isMountedRef = useRef(true);
  const listboxId = useId();
  const hasApiKey = Boolean(getApiKey());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  /* Sync parent `value` only — omit inputValue from deps so local typing is not overwritten before parent updates. */
  useEffect(() => {
    if (!isSelectingRef.current && value !== inputValue) {
      setInputValue(value || "");
    }
  }, [value]);

  useEffect(() => {
    const key = getApiKey();
    if (!key) return;
    loadGoogleMapsScript(key)
      .then((places) => {
        if (isMountedRef.current && places) setPlacesReady(true);
      })
      .catch((err) => {
        if (isMountedRef.current) {
          ErrorHandler.logError(`${logContext}.loadScript`, err, {
            hint: isCity ? "City Places autocomplete will not be available." : "Places autocomplete will not be available.",
          });
        }
      });
  }, [logContext, isCity]);

  const clearSuggestions = useCallback(() => {
    setPredictions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
  }, []);

  const runSearch = useCallback(
    (query) => {
      const key = getApiKey();
      if (!key || !placesReady) return;
      const trimmed = (query || "").trim();
      if (trimmed.length < minQueryLength) {
        clearSuggestions();
        return;
      }
      setIsLoading(true);
      const places = window.google?.maps?.places;
      if (!places?.AutocompleteService) {
        clearSuggestions();
        setIsLoading(false);
        return;
      }
      try {
        const service = new places.AutocompleteService();
        const statusOk = window.google?.maps?.places?.PlacesServiceStatus?.OK ?? "OK";
        const request = isCity ? { input: trimmed, types: ["(cities)"] } : { input: trimmed };
        service.getPlacePredictions(request, (results, status) => {
          if (!isMountedRef.current) return;
          setIsLoading(false);
          const isOk = status === statusOk || status === "OK";
          if (isOk && Array.isArray(results) && results.length > 0) {
            setPredictions(results);
            setShowSuggestions(true);
            setActiveIndex(-1);
          } else {
            if (!isOk && status !== "ZERO_RESULTS") {
              ErrorHandler.logError(
                `${logContext}.search`,
                new Error(`Places autocomplete status: ${status}`),
                {
                  query: trimmed,
                  status: String(status),
                  hint: status === "REQUEST_DENIED" ? REQUEST_DENIED_HINT : "Check Google Cloud Console for this API key.",
                }
              );
            }
            clearSuggestions();
          }
        });
      } catch (err) {
        ErrorHandler.logError(`${logContext}.search`, err, { query: trimmed });
        clearSuggestions();
        setIsLoading(false);
      }
    },
    [placesReady, minQueryLength, isCity, logContext, clearSuggestions]
  );

  /* Re-search when script loads or field gains focus with enough text; omit inputValue from deps — keystrokes use debounce in handleInputChange. */
  useEffect(() => {
    if (!placesReady || inputValue.trim().length < minQueryLength || !hasFocused) return;
    runSearch(inputValue);
  }, [placesReady, hasFocused, minQueryLength, runSearch]);

  const finishSelecting = useCallback(() => {
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 200);
  }, []);

  const getPlaceDetails = useCallback(
    (placeId) => {
      const key = getApiKey();
      if (!key || !placesReady) return;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      const places = window.google?.maps?.places;
      const container = placesServiceRef.current;
      if (!places?.PlacesService || !container) return;
      try {
        setIsLoading(true);
        const service = new places.PlacesService(container);
        service.getDetails({ placeId, fields: DETAIL_FIELDS }, (place, status) => {
          if (!isMountedRef.current) return;
          setIsLoading(false);
          if (status !== "OK" || !place) {
            if (status === "REQUEST_DENIED") {
              ErrorHandler.logError(`${logContext}.getPlaceDetails`, new Error("Places API request denied"), {
                placeId,
                hint: "Check API key and ensure Places API is enabled in Google Cloud.",
              });
            }
            setShowSuggestions(false);
            setPredictions([]);
            finishSelecting();
            return;
          }
          const placeData = isCity ? buildCityPlaceData(place) : buildPlaceData(place);
          const displayValue = isCity
            ? placeData.displayName || placeData.formattedAddress
            : placeData.formattedAddress;
          isSelectingRef.current = true;
          setInputValue(displayValue);
          setShowSuggestions(false);
          setPredictions([]);
          onChange?.(displayValue);
          onPlacePayload?.(placeData);
          finishSelecting();
        });
      } catch (err) {
        ErrorHandler.logError(`${logContext}.getPlaceDetails`, err, { placeId });
        setShowSuggestions(false);
        setPredictions([]);
        setIsLoading(false);
        finishSelecting();
      }
    },
    [placesReady, isCity, onChange, onPlacePayload, logContext, finishSelecting]
  );

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputValue(text);
    onChange?.(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text), 300);
  };

  const handleSelectPrediction = (prediction) => {
    if (!prediction?.place_id) return;
    setShowSuggestions(false);
    setPredictions([]);
    const interim = prediction.description || "";
    setInputValue(interim);
    onChange?.(interim);
    getPlaceDetails(prediction.place_id);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || predictions.length === 0) {
      if (e.key === "Escape") setShowSuggestions(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < predictions.length - 1 ? i + 1 : i));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : -1));
      return;
    }
    if (e.key === "Enter" && activeIndex >= 0 && predictions[activeIndex]) {
      e.preventDefault();
      handleSelectPrediction(predictions[activeIndex]);
      return;
    }
    if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  useLayoutEffect(() => {
    if (!showSuggestions || predictions.length === 0 || !containerRef.current) {
      setDropdownRect(null);
      return;
    }
    const updateRect = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownRect({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [showSuggestions, predictions.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);
      if (!inContainer && !inDropdown) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const predictionKeyPrefix = isCity ? "city-prediction" : "prediction";

  return (
    <div ref={containerRef} className="relative">
      <div ref={placesServiceRef} style={{ display: "none" }} aria-hidden="true" />
      <Input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setHasFocused(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        maxLength={maxLength}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        aria-autocomplete="list"
        aria-expanded={showSuggestions && predictions.length > 0}
        aria-controls={showSuggestions ? listboxId : undefined}
      />
      {isLoading && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"
          aria-hidden
        >
          …
        </span>
      )}
      {!hasApiKey && (
        <p className="text-xs text-muted-foreground mt-1" role="status">
          {noApiKeyMessage}
        </p>
      )}
      {showSuggestions && predictions.length > 0 && dropdownRect &&
        createPortal(
          <ul
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            data-runcards-places-dropdown=""
            className="fixed rounded-md border border-input bg-popover py-1 shadow-lg max-h-60 overflow-auto z-[10000] pointer-events-auto"
            style={{
              top: dropdownRect.top + 1,
              left: dropdownRect.left,
              width: Math.max(dropdownRect.width, 200),
            }}
          >
            {predictions.map((item, index) => (
              <li
                key={item.place_id ?? `${predictionKeyPrefix}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`cursor-pointer px-3 py-2 text-sm select-none hover:bg-accent hover:text-accent-foreground ${
                  index === activeIndex ? "bg-accent text-accent-foreground" : ""
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectPrediction(item);
                }}
              >
                {item.description}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}

export function GooglePlacesAddressInput({
  value = "",
  onChange,
  onPlaceSelected,
  placeholder = "Enter address or place name...",
  id,
  className,
  maxLength,
  "aria-label": ariaLabel,
  autoComplete = "off",
}) {
  return (
    <GooglePlacesAutocompleteCore
      variant="address"
      value={value}
      onChange={onChange}
      onPlacePayload={onPlaceSelected}
      placeholder={placeholder}
      id={id}
      className={className}
      maxLength={maxLength}
      aria-label={ariaLabel}
      autoComplete={autoComplete}
    />
  );
}

/**
 * City search via Places Autocomplete restricted to the (cities) collection, then Place Details
 * for a confirmed locality name and region (administrative_area_level_1).
 */
export function GooglePlacesCityInput({
  value = "",
  onChange,
  onPlaceConfirmed,
  placeholder = "Search for a city…",
  id,
  className,
  "aria-label": ariaLabel,
  autoComplete = "off",
}) {
  return (
    <GooglePlacesAutocompleteCore
      variant="city"
      value={value}
      onChange={onChange}
      onPlacePayload={onPlaceConfirmed}
      placeholder={placeholder}
      id={id}
      className={className}
      aria-label={ariaLabel}
      autoComplete={autoComplete}
    />
  );
}

export default GooglePlacesAddressInput;
